import { onNotification, SuppressNexus, TelemetryLogger } from "@microsoft/oteljs";
import { BeforeUnload } from "./BeforeUnload";
import { LogHandlerType } from "./types/LogHandlerType";
// Utilities
import findLastIndex from "lodash/findLastIndex";
import { createEventIdentifier, getEventFlags, getValidatedAppName, mapToNewEvents } from "./LoggingHelper";
import { scrubUrlsInDataFields } from "./utilities/UrlScrubber";
import { capOversizedDataFields } from "./utilities/TelemetrySizeGuard";
// Constants
import { LogTenantIdentifier } from "./constants/TelemetryConstants";
/** Tenant name or project's namespace shared by Project Web and Planner (Web & Mobile)*/ export const LogTenantName = "Office.ProjectPlanner";
/** Legacy event types from Planner and Project */ export var LegacyEventType = /*#__PURE__*/ function(LegacyEventType) {
    /* Action event  */ LegacyEventType["Action"] = "Action";
    /* Auth error event */ LegacyEventType["AuthError"] = "AuthError";
    /* Engagement event  */ LegacyEventType["Engagement"] = "Engagement";
    /* EUPL event  */ LegacyEventType["Eupl"] = "EUPL";
    /* Diagnostics event  */ LegacyEventType["Diagnostics"] = "Diagnostics";
    /* Pageinit event  */ LegacyEventType["PageInit"] = "PageInit";
    /* Page transition time event  */ LegacyEventType["Ptt"] = "PTT";
    /* Render measurement event  */ LegacyEventType["Render"] = "Render";
    /* RequireJs error event  */ LegacyEventType["RequireJSError"] = "RequireJSError";
    /* Route event  */ LegacyEventType["Route"] = "Route";
    /** Trace event */ LegacyEventType["Trace"] = "Trace";
    /* Qos event  */ LegacyEventType["Qos"] = "Qos";
    /* Unhandled error event  */ LegacyEventType["UnhandledError"] = "UnhandledError";
    /* Survey event  */ LegacyEventType["Survey"] = "Survey";
    return LegacyEventType;
}({});
/** App name used in telemetry event identifiers (e.g. Office.ProjectPlanner.{TelemetryAppName}.Qos) */ export var TelemetryAppName = /*#__PURE__*/ function(TelemetryAppName) {
    TelemetryAppName["Project"] = "Project";
    TelemetryAppName["Planner"] = "Planner";
    return TelemetryAppName;
}({});
/** Type of an event */ export var EventType = /*#__PURE__*/ function(EventType) {
    /* Action event  */ EventType["Action"] = "Action";
    /* Diagnostics event  */ EventType["Diagnostics"] = "Diagnostics";
    /* Engagement event  */ EventType["Engagement"] = "Engagement";
    /* Performance event  */ EventType["Performance"] = "Performance";
    /* Qos event  */ EventType["Qos"] = "Qos";
    /* Copilot event  */ EventType["Copilot"] = "Copilot";
    /* Asha event  */ EventType["Asha"] = "ASHA";
    /* Privacy Concern Event*/ EventType["PrivacyConcern"] = "PrivacyConcern";
    return EventType;
}({});
/**
 * Utility class for UTel Logging
 */ export class UTelLogHandler {
    /**
     * Initialize UTel logging. All event will be captured by UTelLogger and sent to the UTel Tenant.
     * @param telemetrySink Telemetry sink to be used to send events
     * @param options Options object containing all relevant info needed
     * @param [enableConsoleLogging] Indicates if console logging is enabled
     * @param [flushEvents] If provided, this callback will be called when the page is unloaded
     */ initialize(telemetrySink, options, enableConsoleLogging, flushEvents) {
        this.appName = getValidatedAppName(options.appName);
        this.disablePreSendTruncation = options.disablePreSendTruncation ?? false;
        if (enableConsoleLogging) {
            this.ConsoleLogging = true;
        }
        this.Logger = new TelemetryLogger(undefined, options.persistentDataFields ?? []);
        // Note: Nexus token was used when sending data on the legacy pipeline, Nexus.
        // The pipeline is pretty much gone today, but there is some code update
        // needed on UTel's side to safely remove its artifacts like NexusTenantToken.
        // That will happen in FY23 at some point. For now, suppress it.
        this.Logger.setTenantToken(LogTenantName, LogTenantIdentifier, SuppressNexus);
        this.Logger.addSink(telemetrySink);
        this.ConsoleLogging && onNotification().addListener((event)=>{
            // using console.debug to avoid being hijacked by TT logger
            // eslint-disable-next-line no-console
            console.debug(event.message());
        });
        if (flushEvents) {
            BeforeUnload.init();
            BeforeUnload.registerHandler((unload)=>{
                unload && flushEvents();
                return null;
            });
        }
    }
    /**
     * Log an event
     * @param event Event to be logged
     */ log(event) {
        if (!this.Logger || event == null) {
            return;
        }
        // Mapping to new events incase of legacy Planner and Project events
        const eventType = mapToNewEvents(event.type);
        if (eventType == null) {
            return;
        }
        const utelEvent = {
            eventName: createEventIdentifier(eventType, this.appName),
            eventFlags: getEventFlags(),
            dataFields: this.capOversizedPayload(scrubUrlsInDataFields(event.payload) ?? [])
        };
        this.Logger.sendTelemetryEvent(utelEvent);
    }
    /**
     * Truncates oversized event payloads before they are sent so a single large event cannot be
     * rejected by OneCollector with HTTP 413 (Content Too Large). Controlled by the
     * `disablePreSendTruncation` init option (sourced from the `DisablePreSendTelemetryTruncation`
     * change gate on the main thread; default off = truncation enabled).
     * @param dataFields Scrubbed data fields for the event
     * @returns The same fields when within the size limit, otherwise a truncated copy
     */ capOversizedPayload(dataFields) {
        if (this.disablePreSendTruncation) {
            return dataFields;
        }
        return capOversizedDataFields(dataFields) ?? dataFields;
    }
    /**
     * Set a persistent data field that will be sent with every event
     * @param dataField Data field to be set
     */ setPersistentDataField(dataField) {
        if (this.Logger) {
            const persistentDataFields = this.Logger.persistentDataFields;
            // The last-added data field wins, if the same-named data field is added more than once.
            const dataFieldIndex = findLastIndex(persistentDataFields, (field)=>field.name === dataField.name);
            if (dataFieldIndex >= 0) {
                persistentDataFields[dataFieldIndex] = dataField;
            } else {
                persistentDataFields.push(dataField);
            }
        }
    }
    constructor(){
        /** Local UTel logger to be used to send session start/end events */ this.Logger = null;
        /** Telemetry logger type (Aria, UTel) */ this.type = LogHandlerType.UTel;
        /** App name for event identifiers */ this.appName = "Planner";
        /** Console logging. Turned off by default */ this.ConsoleLogging = false;
        /** When true, skip pre-send truncation of oversized event payloads. Set at initialization. */ this.disablePreSendTruncation = false;
    }
}
